import { useEffect, useState } from "react";
import {
  getFormationCertification,
  type FormationCertification,
  type Parcours,
} from "@/lib/formationApi";

/**
 * Garde de formation certifiante (parcours AVOCAT / ARBITRE).
 *
 * Activation PAR PARCOURS : un parcours inerte (`false`) ne fait aucun appel
 * réseau et laisse l'accès libre. Temporisation arbitre : le gating ARBITRE
 * reste inerte tant que les arbitres ne sont pas formés (éviter de bloquer la
 * rédaction de sentence / l'ancrage). Passer `ARBITRE` à `true` pour activer.
 */
export const FORMATION_GATING_ACTIVE: Record<Parcours, boolean> = {
  AVOCAT: true,
  ARBITRE: false,
};

export const FEATURES_PAR_PARCOURS: Record<Parcours, string[]> = {
  AVOCAT: [
    "conclusions_companion",
    "depot_pieces",
    "correspondance_protocolaire",
    "analyse_post_sentence",
  ],
  ARBITRE: ["sentence_draft", "decision_validation", "anchoring"],
};

export function useFormationGuard(parcours: Parcours = "AVOCAT") {
  const actif = FORMATION_GATING_ACTIVE[parcours];
  const [certification, setCertification] =
    useState<FormationCertification | null>(null);
  const [loading, setLoading] = useState(actif);

  useEffect(() => {
    if (!actif) return; // inerte : aucun appel reseau
    let cancelled = false;
    getFormationCertification(parcours)
      .then((c) => {
        if (!cancelled) setCertification(c);
      })
      .catch(() => {
        // Fail-open : une erreur (endpoint indisponible) ne doit pas verrouiller.
        if (!cancelled) setCertification(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [parcours, actif]);

  const canAccessFeature = (feature: string): boolean => {
    if (!actif) return true;
    if (!FEATURES_PAR_PARCOURS[parcours].includes(feature)) return true;
    return certification?.certifie === true;
  };

  return { certification, loading, canAccessFeature };
}
